import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Icon, InputGroup, Tag, Text, Tooltip } from "@blueprintjs/core";
import type { IconName } from "@blueprintjs/icons";
import type { BrowserContents, BrowserGateway, BrowserQuery } from "./ports";
import "./compass-tokens.css";
import "./compass-container.css";

// INSTALLED FROM foundry-ui, unmodified except for the two import specifiers a
// non-shadcn consumer needs. It is a registry item: copied, not depended on.
//
// I was hand-writing this chrome — a tab bar, a breadcrumb, a quick-filters card —
// against the same capture this was built from. foundry-ui had already done it,
// hand-refined, and its header records a mistake I would have repeated: the
// quick-filters strip has ONE "Hide" and a per-card "Apply", not a dismissible
// card each, "which is what an earlier draft of this file invented".
//
// @hand-refined — scripts/gen-component.mjs will not overwrite this file.
//
// Compass, the BROWSER archetype: "breadcrumb and New action, dismissible quick-filter cards, global
// search, a faceted rail and a sortable file table".
//
// BUILT WITH THE CAPTURE OPEN — fidelity/references/foundry-layouts-compass-browser.png. Every
// structure here is in that image: the icon-led breadcrumb with a green New split-button; the Quick
// filters strip with its single "Hide" and per-card "Apply" (NOT a dismissible card each, which is
// what an earlier draft of this file invented); the pill-shaped global search across the full width;
// the rail headed "Filters (0)" with a collapse control, whose sections carry their own search boxes
// and a "View all (41)"; and the table's own six columns with sort carets on two of them.
//
// It implements the SHARED browser port — the same interface the Files folder implements, from a
// different app cut by a different team. The two screens look nothing alike and take the same data,
// which only works because rows are keyed by COLUMN: Compass's six headings and the Files folder's
// three are both just `columns`, and neither screen has to pretend to have the other's fields.
//
// Sorting is local and narrowing is not. Which side of the port each falls on is the whole design
// question: a consumer with a million files cannot filter them in the browser, so search and filter
// selection go through `load()`; the column the user clicked is view state no backend needs.
//
// @example  after `npx shadcn add @foundry/compass-container`:
//   import { CompassContainer } from "./compass-container";
//   import { staticCompassGateway } from "./adapter.static";
//   <CompassContainer gateway={staticCompassGateway} />
//   // alt data in ./adapters/: compassHomeGateway, compassDataCatalogGateway, compassSharedGateway, compassTrashGateway

export interface CompassContainerProps {
  readonly gateway: BrowserGateway;
  /** The green New action. Omit it and the button is not rendered. */
  readonly onCreate?: () => void;
}

export function CompassContainer({ gateway, onCreate }: CompassContainerProps) {
  const [data, setData] = useState<BrowserContents | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Record<string, readonly string[]>>({});
  const [quickHidden, setQuickHidden] = useState(false);
  const [sort, setSort] = useState<{ key: string; asc: boolean } | null>(null);

  // `load` may be sync or async — a static adapter returns a value, a real one returns a promise.
  // Resolving both through the same path is what keeps the port honest: if the block only worked
  // with one of the two, consumers would have to match the fixture's shape rather than the interface.
  const query: BrowserQuery = useMemo(() => ({ search, filters: selected }), [search, selected]);
  useEffect(() => {
    let live = true;
    Promise.resolve(gateway.load(query)).then((d) => { if (live) setData(d); });
    return () => { live = false; };
  }, [gateway, query]);

  if (!data) return <div className="fdy-compass" aria-busy="true" />;

  const toggle = (group: string, option: string) =>
    setSelected((prev) => {
      const on = prev[group] ?? [];
      const next = on.includes(option) ? on.filter((o) => o !== option) : [...on, option];
      return next.length
        ? { ...prev, [group]: next }
        : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== group));
    });

  const cellOf = (row: BrowserContents["rows"][number], key: string) =>
    key === "name" ? row.label : row.cells?.[key] ?? "";

  const rows = sort
    ? [...data.rows].sort((a, b) => {
        const cmp = cellOf(a, sort.key).localeCompare(cellOf(b, sort.key));
        return sort.asc ? cmp : -cmp;
      })
    : data.rows;

  // "Filters (0)" — the capture shows the count of ACTIVE filters, not of available ones.
  const activeCount = Object.values(selected).reduce((n, v) => n + v.length, 0);

  return (
    <div className="fdy-compass">
      <header className="fdy-compass__top">
        <nav className="fdy-compass__crumbs" aria-label="Breadcrumb">
          {data.breadcrumb.map((c, i) => (
            <span key={`${c.label}-${i}`} className="fdy-compass__crumb">
              {i > 0 && <Icon icon="chevron-right" size={14} className="fdy-compass__sep" />}
              {c.icon && <Icon icon={c.icon as IconName} size={14} className="fdy-compass__crumb-icon" />}
              {c.href ? <a href={c.href}>{c.label}</a> : <span>{c.label}</span>}
              {i === data.breadcrumb.length - 1 && <Icon icon="caret-down" size={14} className="fdy-compass__crumb-caret" />}
            </span>
          ))}
        </nav>
        {onCreate && (
          // Green, and a split button. Foundry's primary create action is `intent="success"`
          // everywhere it appears — Compass, the Files folder and Global Branching all show it.
          <div className="fdy-compass__new">
            <Button intent="success" icon="plus" onClick={onCreate}>{data.newLabel ?? "New"}</Button>
            <Button intent="success" icon="caret-down" aria-label="More create options" onClick={onCreate} />
          </div>
        )}
      </header>

      {data.quickFilters && data.quickFilters.length > 0 && !quickHidden && (
        <Card className="fdy-compass__quick" elevation={0}>
          <div className="fdy-compass__quick-head">
            <span className="fdy-compass__quick-title">Quick filters</span>
            {/* One Hide for the strip, which is what the capture has. */}
            <Button minimal small intent="primary" onClick={() => setQuickHidden(true)}>Hide</Button>
          </div>
          <div className="fdy-compass__quick-cards">
            {data.quickFilters.map((q) => (
              <Card key={q.title} className="fdy-compass__quick-card" elevation={0}>
                <div className="fdy-compass__quick-card-head">
                  <span className="fdy-compass__quick-card-title">
                    {q.icon && <Icon icon={q.icon as IconName} size={14} />}
                    {q.title}
                  </span>
                  <Button minimal small intent="primary" onClick={() => toggle("Quick filters", q.title)}>
                    {q.applyLabel ?? "Apply"}
                  </Button>
                </div>
                <p className="fdy-compass__quick-card-description">{q.description}</p>
              </Card>
            ))}
          </div>
        </Card>
      )}

      <InputGroup
        className="fdy-compass__search"
        large
        round
        leftIcon="search"
        placeholder={data.searchPlaceholder ?? "Search"}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <div className="fdy-compass__body">
        <aside className="fdy-compass__rail" aria-label="Filters">
          <div className="fdy-compass__rail-head">
            <span className="fdy-compass__rail-title">
              <Icon icon="filter" size={14} />
              Filters
            </span>
            <Tag minimal round>{activeCount}</Tag>
            <span className="fdy-compass__spacer" />
            <Button minimal small icon="menu-closed" aria-label="Collapse filters" />
          </div>

          {/*
            Each facet is named from its own heading. Three unnamed regions in
            the filter rail are announced as three bare "region"s — the
            category and not which one, which is the whole information the
            heading carries visually.
          */}
          {(data.filters ?? []).map((f) => (
            <section key={f.name} aria-label={f.name} className="fdy-compass__facet">
              <header className="fdy-compass__facet-head">
                <span className="fdy-compass__facet-name">{f.name}</span>
                <Icon icon="chevron-up" size={14} className="fdy-compass__facet-caret" />
              </header>

              {f.searchPlaceholder && (
                <InputGroup className="fdy-compass__facet-search" small leftIcon="search" placeholder={f.searchPlaceholder} />
              )}

              {f.options.map((o) => (
                <Checkbox
                  key={o.label}
                  className="fdy-compass__facet-option"
                  checked={(selected[f.name] ?? []).includes(o.label)}
                  onChange={() => toggle(f.name, o.label)}
                  labelElement={
                    <span className="fdy-compass__facet-row">
                      {o.icon && <Icon icon={o.icon as IconName} size={14} className="fdy-compass__facet-icon" />}
                      <span className="fdy-compass__facet-label">{o.label}</span>
                      {o.count !== undefined && <span className="fdy-compass__facet-count">{o.count}</span>}
                    </span>
                  }
                />
              ))}

              {f.total !== undefined && f.total > f.options.length && (
                <Button minimal small intent="primary" className="fdy-compass__facet-all">{`View all (${f.total})`}</Button>
              )}
            </section>
          ))}
        </aside>

        <div className="fdy-compass__table-wrap">
          <table className="fdy-compass__table">
            <thead>
              <tr>
                {data.columns.map((c) => (
                  <th key={c.key} className={c.sortable ? "is-sortable" : undefined}>
                    <button
                      type="button"
                      className="fdy-compass__th"
                      disabled={!c.sortable}
                      onClick={() => c.sortable && setSort((s) => ({ key: c.key, asc: s?.key === c.key ? !s.asc : true }))}
                    >
                      {c.label}
                      {c.info && <Icon icon="info-sign" size={12} className="fdy-compass__th-info" title={c.info} />}
                      {c.sortable && (
                        <Icon
                          icon={sort?.key === c.key ? (sort.asc ? "caret-up" : "caret-down") : "double-caret-vertical"}
                          size={12}
                          className="fdy-compass__th-sort"
                        />
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  {data.columns.map((c, i) => (
                    <td key={c.key}>
                      {i === 0 ? (
                        <div className="fdy-compass__name-cell">
                          {r.icon && <Icon icon={r.icon as IconName} size={16} className="fdy-compass__row-icon" />}
                          <div className="fdy-compass__name-text">
                            <Text ellipsize tagName="div" className="fdy-compass__name">
                              {r.href ? <a href={r.href}>{r.label}</a> : r.label}
                            </Text>
                            {r.secondary && (
                              <Text ellipsize tagName="div" className="fdy-compass__path">{r.secondary}</Text>
                            )}
                          </div>
                        </div>
                      ) : c.term ? (
                        // A TOOLTIP-INDICATED TERM, not an overflow aid.
                        //
                        // Measured on the live app rather than guessed: Compass mounts 41 Blueprint
                        // Tooltips and NOT ONE of them wraps truncated text. Thirty-five wrap a
                        // <strong class="bp6-tooltip-indicator"> around the YOUR ROLE value — the
                        // dotted-underline affordance for a word that needs defining. The earlier
                        // assumption here was that Foundry tooltips the names it ellipsizes, and
                        // the fiber walk says it does not.
                        <Tooltip content={c.term} compact>
                          <strong className="bp6-ui-text bp6-tooltip-indicator">{r.cells?.[c.key] ?? ""}</strong>
                        </Tooltip>
                      ) : (
                        <Text ellipsize>{r.cells?.[c.key] ?? ""}</Text>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={data.columns.length} className="fdy-compass__empty">Nothing matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
